import { ActionCompleted } from '@/components/animations';
import { Button, LinkButton } from '@/components/elements/forms/buttons';
import Header from '@/components/elements/Header/Header';
import { HeaderButtonIdentifier } from '@/components/elements/HeaderButton/HeaderButton.types';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import Icon from '@/components/icons/Icon';
import CheckoutViewLayout from '@/components/layouts/CheckoutViewLayout';
import { useBookingFlowChrome } from '@/features/booking-flow/BookingFlowChromeContext';
import CardWrapper from '@/components/modules/CardWrapper';
import { default as CheckoutDetails } from '@/components/modules/checkout/CheckoutDetails/CheckoutDetails';
import AddToCalendarModal, { useAddToCalendarModal } from '@/components/modules/modals/AddToCalendar';
import EmptyState from '@/components/templates/EmptyState';
import { SMART_LINKS } from '@/constants/smartLinkConstants';
import { isPlaceFavorite, url } from '@/helpers';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import {
  mapConfirmedBookingInfoGiftcards,
  mapConfirmedBookingInfoProducts,
  mapConfirmedBookingInfoServices,
  mapConfirmedBookingToCheckoutDetailsProps,
} from '@/helpers/confirmation';
import { getContainmentMode } from '@/helpers/containmentPolicy';
import { postToHost } from '@/helpers/embed';
import { useAppSelector } from '@/hooks';
import { _s, _t } from '@/locale';
import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Redirect } from 'react-router-dom';
import useConfirmation from './BookingConfirmation.hooks';

const baseTranslationKey = 'pages.booking.confirmation.BookingConfirmation' as const;

const BookingConfirmation = () => {
  const { booking, selectedPaymentMethod, isActivatePaymentFlow, loading, error, redirect, nextStepsAction } =
    useConfirmation();
  const {
    onOpen: handleOpenCalendarModal,
    onClose: onCloseCalendarModal,
    ...addToCalendarProps
  } = useAddToCalendarModal(booking);

  const addPlaceToFavoritesButton = useRef<HTMLButtonElement>(null);
  const bookSameServiceButton = useRef<HTMLButtonElement>(null);
  // Latched, not state: once the place is known not to be a favourite the
  // button stays up after favouriting it, and settling it must not re-render.
  const showAddPlaceToFavorites = useRef(false);
  const user = useAppSelector((state) => state.users)?.user;
  // Contained in the booking-widget SPA, the marketplace routes this page links
  // to (/bokning, /cancel, /places, the app smart links) do not exist — keep
  // only add-to-calendar and book-again. The two contained surfaces then part
  // ways on the way out: the framed widget asks its host to close, a Linked
  // Session leaves for the start page.
  const mode = getContainmentMode();
  const contained = mode !== null;
  const framed = mode === 'embed';
  const linked = mode === 'linked';
  const { attribution } = useBookingFlowChrome();

  useEffect(() => {
    const placeId = booking?.place?.id;
    if (user && placeId) {
      showAddPlaceToFavorites.current ||= !isPlaceFavorite(user, placeId);
    }
  }, [user, booking?.place?.id]);

  // The Linked Session ends because the document unloads; the theme memos stay
  // put, so coming back here keeps the merchant's colours.
  const handleExitToStartPage = () => {
    window.location.href = url.getBaseUrl();
  };

  /**
   * If klarna or qliro confirmation fails (if time is no longer available), client will be redirected back
   * to calendar view with an error message saying time is busy and payment will be released.
   */
  if (redirect) {
    bookingFlowTrackingSession.error(['time_slot_not_available']);
    return <Redirect to={redirect} />;
  }

  if (error) {
    // Contained, a silent return to the root after a payment reads as the
    // booking having vanished — and in a Linked Session there is no route to
    // return to at all. Say what happened, and offer the way out this surface
    // has on the loaded page.
    if (contained) {
      return (
        <div className="p-lg min-h-screen">
          <EmptyState
            title={_t(`${baseTranslationKey}.framedError.title`)}
            body={_t(`${baseTranslationKey}.framedError.body`)}
            cta={
              <Button
                variant="primary"
                size="md"
                label={
                  linked
                    ? _t(`${baseTranslationKey}.footer.cta.exitToStartPage`)
                    : _t(`${baseTranslationKey}.framedError.cta`)
                }
                onClick={linked ? handleExitToStartPage : () => postToHost({ type: 'BD_WIDGET_CLOSE' })}
              />
            }
          />
        </div>
      );
    }

    return <Redirect to="/" />;
  }

  if (loading) {
    return <LoadingPlaceHolder text={_s('confirmationLoader')} />;
  }

  const checkoutDetailsProps = mapConfirmedBookingToCheckoutDetailsProps({ booking, selectedPaymentMethod });
  const services = mapConfirmedBookingInfoServices(booking);
  const giftcards = mapConfirmedBookingInfoGiftcards(booking);
  const products = mapConfirmedBookingInfoProducts(booking);
  const placePageUrl = `/places/${booking.place.about.slug}-${booking.place.id}`;
  const bookingOriginator = sessionStorage.getItem('bookSPurl') ?? placePageUrl;
  const clientEmail = booking.extra?.formfields?.[1]?.email;
  const cancellationCode = booking.services?.find((service) => service.cancellationCode)?.cancellationCode;

  const meetingLink = booking.extra?.meetingLink;
  const showMeetingLink = meetingLink && meetingLink.length > 0;

  const handleOpenMeetingLink = () => {
    const fullMeetingLink = meetingLink && meetingLink.startsWith('https://') ? meetingLink : `https://${meetingLink}`;
    window.open(fullMeetingLink, '_blank', 'noopener,noreferrer');
  };

  const handleAddPlaceToFavorites = () => {
    addPlaceToFavoritesButton?.current?.blur();
    nextStepsAction().onAddPlaceToFavorites();
  };

  const handleBookSameService = () => {
    bookSameServiceButton?.current?.blur();
    nextStepsAction().onBookSameService();
  };

  const handleCloseCalendarModal = () => {
    onCloseCalendarModal();
  };

  return (
    <CheckoutViewLayout
      header={{
        // A Linked Session has nowhere to close back to — the merchant's flow
        // ends here, and the way out is the start-page exit in the footer.
        leftSlot: linked
          ? undefined
          : {
              button: {
                item: {
                  identifier: HeaderButtonIdentifier.Close,
                  icon: 'close',
                  to: bookingOriginator,
                },
                onClick: (e) => {
                  if (framed) {
                    e.preventDefault();
                    postToHost({ type: 'BD_WIDGET_CLOSE' });
                  }
                },
                'aria-label': _t('close'),
              },
              text: _t('close'),
            },
        middleSlot: {
          title: _t(`${baseTranslationKey}.page.title`),
          subtitle: checkoutDetailsProps.placeName,
        },
        rightSlot: {
          ...(checkoutDetailsProps.logo && { imgUrl: checkoutDetailsProps.logo }),
        },
      }}
      wrapperClass="bg-surface_mp lg:bg-gradient-surface-mp min-h-viewport">
      <div className="bg-surface_mp lg:bg-transparent">
        <div className="lg:py-xxl lg:container">
          <div className="gap-xxl flex items-start">
            <div className="gap-lg flex grow-[9999] basis-[600px] flex-col">
              <CardWrapper className="bg-white">
                <EmptyState
                  icon={<ActionCompleted style={{ width: 196, height: 154 }} />}
                  title={_s(`${baseTranslationKey}.header${isActivatePaymentFlow ? '.activatePayment' : ''}.title`)}
                  titleClassName="text-h-m"
                  className="gap-xs"
                  body={
                    <span className="space-y-lg text-l flex flex-col">
                      {!user && cancellationCode && (
                        <span className="flex flex-col">
                          <span>
                            {_s(`${baseTranslationKey}.header.body.cancellationCode`)}{' '}
                            <span className="font-semibold">{cancellationCode}</span>
                          </span>
                          {!contained && (
                            <>
                              <span>{_s(`${baseTranslationKey}.header.body.cancellationLink`)}</span>
                              <Link to={`/cancel?code=${cancellationCode}`}>{url.getBaseUrl()}cancel</Link>
                            </>
                          )}
                        </span>
                      )}
                      {clientEmail && <p>{_s(`${baseTranslationKey}.header.body.email`, { email: clientEmail })}</p>}
                    </span>
                  }
                />
              </CardWrapper>
              {checkoutDetailsProps && (
                <div className="lg:hidden">
                  {
                    <CardWrapper className="bg-white">
                      <CheckoutDetails
                        {...checkoutDetailsProps}
                        services={services}
                        giftcards={giftcards}
                        products={products}
                      />
                    </CardWrapper>
                  }
                </div>
              )}
              {!contained && (
                <CardWrapper className="bg-surface_l3 border-border_bold !border-1">
                  <div className="p-lg">
                    <div className="p-sm">
                      <Header className="text-center" label={_s(`${baseTranslationKey}.appDownload.title`)} size="xl" />
                    </div>
                    <p className="text-fg_secondary text-m text-center">
                      {_s(`${baseTranslationKey}.appDownload.body`)}
                    </p>
                    <div className="pt-lg flex items-center justify-center">
                      <a className="align-center justify-left flex flex-wrap" href={SMART_LINKS.CONFIRMED}>
                        <img
                          src="/images/download-app-store.png"
                          className="mr-3 h-[40px]"
                          alt={_s('availableAppStore')}
                        />
                        <img
                          src="/images/download-google-play.png"
                          className="h-[40px]"
                          alt={_s('availablePlayStore')}
                        />
                      </a>
                    </div>
                  </div>
                </CardWrapper>
              )}
              <CardWrapper className="bg-white">
                <div className="p-lg gap-lg flex flex-col">
                  <Button
                    block
                    variant="primary"
                    leftIcon={<Icon variant="calendar-check" />}
                    label={_s(`${baseTranslationKey}.footer.cta.addToCalendar`)}
                    onClick={handleOpenCalendarModal}
                    size="md"
                  />
                  {showMeetingLink && (
                    <Button
                      block
                      variant="secondary"
                      leftIcon={<Icon variant="video-call" />}
                      label={_s('videoMeeting')}
                      onClick={handleOpenMeetingLink}
                      size="md"
                    />
                  )}
                  {!contained && showAddPlaceToFavorites.current && (
                    <Button
                      ref={addPlaceToFavoritesButton}
                      block
                      onClick={handleAddPlaceToFavorites}
                      variant="secondary"
                      leftIcon={
                        <Icon
                          {...(isPlaceFavorite(user, booking?.place?.id) && { color: 'danger-500' })}
                          variant={isPlaceFavorite(user, booking?.place?.id) ? 'heart-filled' : 'heart'}
                        />
                      }
                      label={
                        isPlaceFavorite(user, booking?.place?.id)
                          ? _s(`${baseTranslationKey}.footer.cta.addToFavorites.remove`)
                          : _s(`${baseTranslationKey}.footer.cta.addToFavorites.add`)
                      }
                      size="md"
                    />
                  )}
                  {(contained || !showAddPlaceToFavorites.current) && !isActivatePaymentFlow && (
                    <Button
                      ref={bookSameServiceButton}
                      block
                      onClick={handleBookSameService}
                      variant="secondary"
                      leftIcon={<Icon variant="calendar-plus" />}
                      label={_s(`${baseTranslationKey}.footer.cta.bookSameServiceAgain`)}
                      size="md"
                    />
                  )}
                  {!contained && user && (
                    <LinkButton
                      block
                      to={`/bokning/${booking.id}`}
                      variant="secondary"
                      leftIcon={<Icon variant="calendar-empty" />}
                      label={_s(`${baseTranslationKey}.footer.cta.viewBookingDetails`)}
                      size="md"
                    />
                  )}
                  {linked && (
                    <Button
                      block
                      onClick={handleExitToStartPage}
                      variant="link"
                      label={_s(`${baseTranslationKey}.footer.cta.exitToStartPage`)}
                      size="md"
                    />
                  )}
                  {!contained && (
                    <LinkButton
                      to={bookingOriginator}
                      block
                      variant="link"
                      label={_s(`${baseTranslationKey}.footer.cta.close`)}
                      size="md"
                    />
                  )}
                </div>
              </CardWrapper>
            </div>
            <div className="gap-xs hidden lg:flex lg:grow-[100] lg:basis-[390px] lg:flex-col">
              <CardWrapper className="w-full bg-white">
                {checkoutDetailsProps && (
                  <CheckoutDetails
                    {...checkoutDetailsProps}
                    services={services}
                    giftcards={giftcards}
                    products={products}
                  />
                )}
              </CardWrapper>
              {attribution?.('column')}
            </div>
          </div>
        </div>
      </div>
      {attribution ? <div className="lg:hidden">{attribution('bar')}</div> : null}
      <AddToCalendarModal onClose={handleCloseCalendarModal} {...addToCalendarProps} />
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
    </CheckoutViewLayout>
  );
};

export default BookingConfirmation;
